import { PanelShell } from '@ayble-health/dashboard-components';
import React, { useCallback, useEffect, useMemo } from 'react';
import { MeetingTypePicker } from './MeetingTypePicker';
import { AppointmentSchedulerView } from './types';
import { useSelectMeetingType } from './hooks/useSelectMeetingType';
import { MeetingDatePicker } from './MeetingDatePicker';
import { useScheduleAppointment } from '../../api/careTeam/useScheduleAppointment';
import { useUpdateMeetingStatus } from '../../api/healthCoach/mutations/useUpdateMeetingStatus';
import { MeetingType } from '../../models/meeting';
import { ProviderType } from '../../api/types';

type Props = {
  currentView: AppointmentSchedulerView;
  goToDatePicker: (
    providerType: ProviderType,
    meetingType: MeetingType
  ) => void;
  goToMeetingTypePicker: () => void;
  highlightedMeetingId: number | null;
  requestedProviderType?: ProviderType | null;
  selectedMeetingType: MeetingType | null;
  selectedProviderType: ProviderType | null;
  onRequestedProviderTypeHandled?: () => void;
};

export const AppointmentScheduler = ({
  currentView,
  goToDatePicker,
  goToMeetingTypePicker,
  highlightedMeetingId,
  requestedProviderType,
  selectedMeetingType,
  selectedProviderType,
  onRequestedProviderTypeHandled,
}: Props) => {
  const {
    appointmentOptions,
    isError,
    isLoading,
    userId,
  } = useSelectMeetingType();

  const { mutation } = useScheduleAppointment();
  const { mutation: updateMeetingStatus } = useUpdateMeetingStatus();

  const isSelectingVisitType =
    currentView === AppointmentSchedulerView.MeetingTypePicker;

  const isSelectingDate =
    currentView === AppointmentSchedulerView.DateTypePicker;

  const selectedCareMemberType = useMemo(
    () =>
      appointmentOptions.find(
        (o) =>
          o.meetingType === selectedMeetingType &&
          o.providerType === selectedProviderType
      )?.careMemberType ?? null,
    [appointmentOptions, selectedMeetingType, selectedProviderType]
  );

  useEffect(() => {
    if (
      !requestedProviderType ||
      isLoading ||
      isError ||
      selectedProviderType
    ) {
      return;
    }

    const matchingOption = appointmentOptions.find(
      (o) => o.providerType === requestedProviderType
    );
    if (!matchingOption) return;

    goToDatePicker(matchingOption.providerType, matchingOption.meetingType);
    onRequestedProviderTypeHandled?.();
  }, [
    appointmentOptions,
    goToDatePicker,
    isError,
    isLoading,
    onRequestedProviderTypeHandled,
    requestedProviderType,
    selectedProviderType,
  ]);

  const onSubmitMeeting = useCallback(
    async (
      selectedIsoString: string,
      // we kept a generic name to indicate an id
      // belonging to a coach or a provider
      hostId: string,
      selectedMeetingTypeId: string
    ) => {
      if (!selectedMeetingTypeId || !userId) return;

      const isEditing = !!highlightedMeetingId;
      const mutateFn = isEditing
        ? updateMeetingStatus.mutateAsync
        : mutation.mutateAsync;

      const requestBody: any = isEditing
        ? {
            meetingId: highlightedMeetingId,
            payload: {
              meeting_type: selectedMeetingTypeId,
              meeting_date: selectedIsoString,
              provider: {
                healthie_id: hostId,
                type: selectedProviderType,
              },
            },
          }
        : {
            meeting_type_id: selectedMeetingTypeId,
            meeting_date: selectedIsoString,
            provider_id: `${hostId}`,
            user_id: userId,
          };

      try {
        await mutateFn(requestBody);

        goToMeetingTypePicker();
      } catch (error: any) {
        console.error('An error occurred while creating the appointment.');
      }
    },
    [
      goToMeetingTypePicker,
      highlightedMeetingId,
      mutation,
      selectedProviderType,
      updateMeetingStatus,
      userId,
    ]
  );

  return (
    <PanelShell>
      {isSelectingVisitType && (
        <MeetingTypePicker
          appointmentOptions={appointmentOptions}
          isError={isError}
          isLoading={
            isLoading || mutation.isLoading || updateMeetingStatus?.isLoading
          }
          onSelectMeetingType={goToDatePicker}
        />
      )}

      {isSelectingDate && (
        <MeetingDatePicker
          careMemberType={selectedCareMemberType}
          isError={mutation?.isError}
          isSubmitting={mutation?.isLoading || updateMeetingStatus?.isLoading}
          meetingType={selectedMeetingType}
          onGoBack={goToMeetingTypePicker}
          onSubmit={onSubmitMeeting}
          providerType={selectedProviderType}
        />
      )}
    </PanelShell>
  );
};
